import React, { useEffect, useRef, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  Calculator,
  Clock,
  History,
  Info,
  Landmark,
  Send,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const LoanApplyDialog = ({
  open,
  onOpenChange,
  mode,
  title,
  maxWidthClass = "max-w-md",
  submitLabel = "Submit",
  loanTypes = [],
  employees = [],
  employeeGrossSalary = 0,
  employeeExistingEmi = 0,
  onDialogOpen,
  onSubmit,
  employeeId,
  formatCurrency = (amt) => `₹${Number(amt || 0).toLocaleString()}`,
}) => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [type, setType] = useState("");
  const [amount, setAmount] = useState("");
  const [tenureMonths, setTenureMonths] = useState("");
  const [purpose, setPurpose] = useState("");
  const [interestRate, setInterestRate] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [submittedOnce, setSubmittedOnce] = useState(false);

  // ✅ Prevent continuous API call while dialog is open (call only once per open)
  const didCallOnOpenRef = useRef(false);

  const {user} = useAuth();
  const navigate = useNavigate();
  // reset when closing
  useEffect(() => {
    if (!open) {
      didCallOnOpenRef.current = false; // ✅ reset for next time
      setSubmittedOnce(false);
      setSubmitting(false);

      setSelectedEmployeeId("");
      setType("");
      setAmount("");
      setTenureMonths("");
      setPurpose("");
      setInterestRate(0);
    }
  }, [open]);

  // ✅ call onDialogOpen only once when dialog opens
  useEffect(() => {
    if (!open) return;
    if (didCallOnOpenRef.current) return;

    didCallOnOpenRef.current = true;
    if (typeof onDialogOpen === "function") onDialogOpen();
  }, [open, onDialogOpen]);

  const selectedEmployee = useMemo(() => {
    if (mode !== "admin") return null;
    return employees.find((e) => e._id === selectedEmployeeId) || null;
  }, [mode, employees, selectedEmployeeId]);

  const grossSalary = useMemo(() => {
    if (mode === "admin") return Number(selectedEmployee?.gross_salary || 0);
    return Number(employeeGrossSalary || 0);
  }, [mode, selectedEmployee, employeeGrossSalary]);

  const existingEmi = useMemo(() => {
    if (mode === "admin") return Number(selectedEmployee?.totalMonthlyEMI || 0);
    return Number(employeeExistingEmi || 0);
  }, [mode, selectedEmployee, employeeExistingEmi]);

  const selectedLoanType = useMemo(() => {
    return loanTypes.find((t) => t.loan_type === type) || null;
  }, [loanTypes, type]);

  // keep interest rate in sync with loan type
  useEffect(() => {
    if (!selectedLoanType) {
      setInterestRate(0);
      return;
    }
    setInterestRate(Number(selectedLoanType.interest_rate || 0));
  }, [selectedLoanType]);

  const calculateEmi = (principal, annualRate, tenure) => {
    const P = Number(principal || 0);
    const N = Number(tenure || 0);
    const annual = Number(annualRate || 0);

    if (!P || !N) return 0;

    const monthlyRate = annual / 100 / 12;

    if (monthlyRate === 0) return Math.round(P / N);

    const factor = Math.pow(1 + monthlyRate, N);
    const emi = (P * monthlyRate * factor) / (factor - 1);
    return Math.round(emi);
  };

  const eligibility = useMemo(() => {
    const gross = Number(grossSalary || 0);
    const existing = Number(existingEmi || 0);
    const principal = Number(amount || 0);
    const N = Number(tenureMonths || 0);
    const annual = Number(interestRate || 0);

    const maxEmiCapacity = gross * 0.5 - existing;
    const safeMax = Math.max(0, maxEmiCapacity);

    if (!gross || !N) {
      return {
        estimatedEmi: 0,
        maxEMI: safeMax,
        maxEligible: 0,
        affordable: true,
        progressPct: 0,
      };
    }

    const estimatedEmi = calculateEmi(principal, annual, N);
    const monthlyRate = annual / 100 / 12;

    let maxLoanAmount = 0;
    if (safeMax > 0) {
      if (monthlyRate === 0) {
        maxLoanAmount = safeMax * N;
      } else {
        const factor = Math.pow(1 + monthlyRate, N);
        maxLoanAmount = safeMax * ((factor - 1) / (monthlyRate * factor));
      }
    }

    maxLoanAmount = Math.floor(maxLoanAmount / 1000) * 1000;
    const affordable = estimatedEmi <= safeMax;

    const progressPct =
      safeMax > 0 ? Math.min(100, (estimatedEmi / safeMax) * 100) : 0;

    return {
      estimatedEmi,
      maxEMI: safeMax,
      maxEligible: Math.max(0, maxLoanAmount),
      affordable,
      progressPct,
    };
  }, [grossSalary, existingEmi, amount, tenureMonths, interestRate]);

  const totalPayable = useMemo(() => {
    const emi = Number(eligibility.estimatedEmi || 0);
    const months = Number(tenureMonths || 0);

    if (!emi || !months) return 0;
    return emi * months;
  }, [eligibility.estimatedEmi, tenureMonths]);

  const totalInterest = useMemo(() => {
    const principal = Number(amount || 0);
    return Math.max(0, totalPayable - principal);
  }, [amount, totalPayable]);

  const isAdmin = mode === "admin";
  const canSubmit =
    type &&
    amount &&
    tenureMonths &&
    purpose &&
    (isAdmin ? selectedEmployeeId : true);

  const allRequiredFilled = useMemo(() => {
    const commonOk =
      Boolean(type) &&
      String(amount).trim() !== "" &&
      String(tenureMonths).trim() !== "" &&
      String(purpose).trim() !== "";

    return isAdmin ? commonOk && Boolean(selectedEmployeeId) : commonOk;
  }, [isAdmin, type, amount, tenureMonths, purpose, selectedEmployeeId]);

  const handleSubmit = async () => {
    setSubmittedOnce(true);

    if (!allRequiredFilled) return;

    if (!eligibility.affordable) {
      toast.error("EMI limit exceeded.");
      return;
    }

    setSubmitting(true);
    try {
      const loanData = {
        loan_type: type,
        amount: parseFloat(amount),
        tenure_months: parseInt(tenureMonths, 10),
        interest_rate: Number(interestRate || 0),
        monthly_emi: eligibility.estimatedEmi,
        purpose: purpose,
        totalPayable: totalPayable,
        totalInterest: totalInterest,
        employee_id: isAdmin ? selectedEmployeeId : employeeId,
      };

      await onSubmit?.(loanData);
      onOpenChange?.(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          maxWidthClass,
          "p-0 max-h-full overflow-y-auto border-none shadow-2xl rounded-xl bg-background text-foreground "
        )}
      >
        {/* HEADER: Matching Leave Request Style */}
        <DialogHeader className="px-6 py-5 border-b border-border">
          <DialogTitle className="text-2xl font-bold tracking-tight">
            {title}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Fill out the details below to calculate and submit your loan
            request.
          </p>
        </DialogHeader>

        <div className="px-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* ADMIN VIEW: EMPLOYEE SELECTOR (Consistent with Leave Admin View) */}
          {isAdmin && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Target Employee <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedEmployeeId}
                onValueChange={setSelectedEmployeeId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees
                    .filter((e) => e.status === "active")
                    .map((emp) => (
                      <SelectItem key={emp._id} value={emp._id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* FINANCIAL CONTEXT: Using your Blue-themed "Calculated Days" style */}
          {!isAdmin && (
            <div className="grid grid-cols-2 gap-4 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
              <div className="space-y-1">
                <span className="text-xs font-medium text-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" /> Monthly Salary
                </span>
                <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                  {formatCurrency(grossSalary)}
                </p>
              </div>
              <div className="space-y-1 text-right">
                <span className="text-xs font-medium text-blue-800 dark:text-blue-300 flex items-center gap-1.5 justify-end">
                  <History className="h-3.5 w-3.5" /> Current EMIs
                </span>
                <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                  {formatCurrency(existingEmi)}
                </p>
              </div>
            </div>
          )}

          {/* INPUT GRID */}
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Loan Type <span className="text-red-500">*</span>
              </Label>

              {loanTypes.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3">
                  <p className="text-sm text-muted-foreground">
                    No loan types configured.
                  </p>

                  {user.role === "admin" && (
                    <Button
                      type="button"
                      variant="link"
                      className="px-0 text-primary"
                      onClick={() => navigate("/settings")}
                    >
                      Add loan types in settings
                    </Button>
                  )}
                </div>
              ) : (
                <Select
                  value={type}
                  onValueChange={(val) => {
                    setType(val);
                    const rate = loanTypes.find(
                      (t) => t.loan_type === val
                    )?.interest_rate;
                    setInterestRate(Number(rate || 0));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select loan type" />
                  </SelectTrigger>

                  <SelectContent>
                    {loanTypes.map((t) => (
                      <SelectItem key={t.loan_type} value={t.loan_type}>
                        {t.loan_type} ({t.interest_rate}% p.a.)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Amount <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Tenure (Months) <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  value={tenureMonths}
                  onChange={(e) => setTenureMonths(e.target.value)}
                  placeholder="e.g. 12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Purpose <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="Reason for loan..."
                rows={3}
                required
              />
            </div>
          </div>

          {/* CALCULATOR: High-Fidelity Feedback using your color logic */}
          {type && amount && tenureMonths && (
            <div className="space-y-4">
              <div
                className={cn(
                  "p-5 border rounded-lg transition-colors flex flex-col gap-4",
                  !eligibility.affordable
                    ? "bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700"
                    : "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700"
                )}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Calculator
                      className={cn(
                        "h-5 w-5",
                        !eligibility.affordable
                          ? "text-red-600"
                          : "text-blue-600"
                      )}
                    />
                    <span
                      className={cn(
                        "text-sm font-bold uppercase tracking-wider",
                        !eligibility.affordable
                          ? "text-red-800 dark:text-red-300"
                          : "text-blue-800 dark:text-blue-300"
                      )}
                    >
                      Estimated EMI
                    </span>
                  </div>
                  <span className="text-xs font-mono opacity-70">
                    Rate: {interestRate}%
                  </span>
                </div>

                <div
                  className={cn(
                    "text-3xl font-extrabold tabular-nums",
                    !eligibility.affordable
                      ? "text-red-700 dark:text-red-400"
                      : "text-blue-700 dark:text-blue-400"
                  )}
                >
                  {formatCurrency(eligibility.estimatedEmi)}
                  <span className="text-sm font-medium opacity-70 ml-1">
                    / month
                  </span>
                </div>

                {/* Warning: Matching your Probation Notice style */}
                {!eligibility.affordable && (
                  <div className="bg-orange-50 dark:bg-orange-900/30 border-l-4 border-orange-500 p-3 rounded-r-lg flex items-center space-x-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-orange-900 dark:text-orange-200">
                        Capacity Warning
                      </p>
                      <p className="text-[11px] text-orange-800 dark:text-orange-400">
                        EMI exceeds 50% of monthly income. Max limit:{" "}
                        {formatCurrency(eligibility.maxEMI)}
                      </p>
                    </div>
                  </div>
                )}

                <Progress
                  value={eligibility.progressPct}
                  className="h-2 bg-zinc-200 dark:bg-zinc-800"
                  indicatorClassName={cn(
                    eligibility.progressPct > 90 ? "bg-red-500" : "bg-blue-600"
                  )}
                />
              </div>

              <div className="flex justify-between px-1 text-[11px] font-bold uppercase text-muted-foreground tracking-widest">
                <span>
                  Interest:{" "}
                  {formatCurrency(
                    eligibility.estimatedEmi * tenureMonths - amount
                  )}
                </span>
                <span>
                  Total:{" "}
                  {formatCurrency(eligibility.estimatedEmi * tenureMonths)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER: Matching Leave Request Buttons */}
        <DialogFooter className="px-6 py-5 border-t border-border flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting || !eligibility.affordable}
            className="w-full sm:w-auto flex items-center space-x-2 font-bold"
          >
            {submitting ? (
              <>
                <Clock className="h-4 w-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>{submitLabel}</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoanApplyDialog;
